import type { ApolloError, MutationFunctionOptions } from '@apollo/client';
import { useMutation } from '@apollo/client';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiRoutes } from '../../constants/api-routes.constants';
import { AUTH_CONSTANTS } from '../../constants/auth.constants';
import { useAuth } from '../../context/AuthContext';
import { LOGIN_MUTATION } from '../../graphql/resolvers/userMutations/login.gql';
import { detectIdvRequired } from '../../helpers/detectIdvRequired';
import { isSafeRedirectPath } from '../../helpers/isSafeRedirectPath';
import { resolvePostLoginRoute } from '../../helpers/resolvePostLoginRoute';
import { initialLoginState } from '../../initial-states/userLogIn.initialState';
import { loginInitialState } from '../../initial-states/userSignUp.initialState';
import type { CoreUserDDB } from '../../interfaces/user.interface';
import { userSlice } from '../../store/user.store';
import { RenderUserLogin } from './RenderUserLogin';
import {
  type AvatarSyncStore,
  syncLoginPayloadAvatar,
} from './syncLoginPayloadAvatar';
import './login-user.scss';

export default function UserLogin(): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const store = useStore() as AvatarSyncStore;
  const { login: authLogin, isSignedIn } = useAuth();

  // TRIL-216: read the original URL preserved by App.tsx's private-route
  // Navigate (when an unauthenticated user is bounced here). The state is
  // in-memory, but isSafeRedirectPath defensively rejects anything that
  // isn't a safe in-app path (non-strings, protocol-relative URLs, etc.).
  const fromState = (location.state as { from?: unknown } | null)?.from;
  const from = isSafeRedirectPath(fromState) ? fromState : null;

  const [loginError, setLoginError] = useState<ApolloError | undefined>();
  const [dashboardRoute, setDashboardRoute] = useState<string>('');
  const [feedRoute, setFeedRoute] = useState<string>('');
  const [interestsRoute, setInterestsRoute] = useState<string>('');
  const [loginPayload, setLoginPayload] =
    useState<CoreUserDDB>(initialLoginState);

  const { LOGIN_USER_INIT, LOGIN_USER_SUCCESS } = userSlice.actions;
  const [loginUser, { loading, error, data }] = useMutation(LOGIN_MUTATION, {
    variables: {
      input: loginInitialState,
    },
    onCompleted: ({ loginUser }) => {
      // TRIL-305: tokens are now typed `string | null` from codegen. Gate the
      // success block on their presence — a login response without tokens is not
      // a success — which also narrows them to `string` for the setItem calls.
      if (loginUser?.accessToken && loginUser.refreshToken) {
        if (loginUser.newUser) {
          const interestsRoute = `${apiRoutes.USER}/${loginUser.username}${apiRoutes.INTERESTS}`;
          setInterestsRoute(interestsRoute);
        }
        const userDashboardURI = `${apiRoutes.USER}/${loginUser.username}${apiRoutes.DASHBOARD}`;
        setDashboardRoute(userDashboardURI);
        const userFeedURI = `${apiRoutes.USER}/${loginUser.username}${apiRoutes.FEED}`;
        setFeedRoute(userFeedURI);
        localStorage.setItem(AUTH_CONSTANTS.JWT_TOKEN, loginUser.accessToken);
        localStorage.setItem(
          AUTH_CONSTANTS.JWT_REFRESH_TOKEN,
          loginUser.refreshToken,
        );
        setLoginPayload(loginUser as unknown as CoreUserDDB);
        authLogin(loginUser);
      }
    },
    onError: (error: ApolloError) => {
      const idv = detectIdvRequired(error);
      if (idv) {
        navigate(apiRoutes.VERIFY_IDENTITY, { state: idv });
        return;
      }
      setLoginError(error);
    },
  });

  useEffect(() => {
    dispatch(LOGIN_USER_INIT());
  }, [dispatch, LOGIN_USER_INIT]);

  useEffect(() => {
    if (!loginPayload.id) return;
    dispatch(LOGIN_USER_SUCCESS(loginPayload));
    // TRIL-141: syncLoginPayloadAvatar reads userAvatarKey from the store
    // at commit time and skips the URL dispatch if it no longer matches the
    // captured key. This catches the stale-write window that exists between
    // a Redux state change and React's commit/effect cleanup, where a
    // pending presign for a prior user could otherwise win the last write.
    void syncLoginPayloadAvatar(store, loginPayload);
  }, [LOGIN_USER_SUCCESS, dispatch, loginPayload, store]);

  useEffect(() => {
    if (!isSignedIn || !loginPayload.id) return;
    // TRIL-216 route decision (newUser onboarding > deep-link `from` >
    // landing-page default) lives in resolvePostLoginRoute so the branch
    // order is unit-tested without the full Apollo/Auth/Redux render (TRIL-227).
    const target = resolvePostLoginRoute({
      newUser: Boolean(loginPayload.newUser),
      from,
      landingPage: loginPayload.userPreferences?.landingPage,
      interestsRoute,
      dashboardRoute,
      feedRoute,
    });
    if (target) navigate(target);
  }, [
    isSignedIn,
    loginPayload,
    from,
    dashboardRoute,
    feedRoute,
    interestsRoute,
    navigate,
  ]);

  // TRIL-305: useMutation now returns a precisely-typed trigger (LoginUserMutation
  // / LoginInput variables). RenderUserLogin is presentational and types this prop
  // loosely; bridge at the boundary rather than thread the generated generics
  // through the form-submit chain.
  const loginUserTrigger = loginUser as unknown as (
    options?: MutationFunctionOptions,
  ) => Promise<unknown>;

  return (
    <RenderUserLogin
      dashboardRoute={dashboardRoute}
      error={error}
      loading={loading}
      loginUser={loginUserTrigger}
    />
  );
}
